'use client'

import { useEffect, useMemo, useState } from 'react'
import { IconCategory, IconList, IconMoodSad2 } from '@tabler/icons-react'
import clsx from 'clsx'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import qs from 'qs'

import { SeoListName } from 'shared/analytics'
import Button from 'shared/ui/Button'
import SelectDropdown from 'shared/ui/form/Select'
import IconButton, {
  type IconButtonProps
} from 'shared/ui/IconButton/IconButton'
import { theme } from 'shared/ui/utils/designTokens'
import { PATHS } from 'shared/utils/paths'
import { createAsPath, searchParamsToObject } from 'shared/utils/searchParams'
import { addTestId } from 'shared/utils/tests'

import ProductCard from '../components/ProductCard'
import { ProductTypes } from '../types/products'
import {
  CategoryAccSearchTypes,
  sortProductsByCategory
} from '../utils/categories'
import { parseQueryFilters } from '../utils/filters'
import {
  getSelectValueBySortingOptions,
  getSortingOptionsBySelectValueForUpselling,
  SORTING_OPTIONS,
  SortingOptionsTypes
} from '../utils/sorting'

import styles from './ProfileFavourites.module.scss'

const ViewChangeWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.viewChangeWrapper}>{children}</div>
)

export const ViewTypeIconWrapper = ({
  $active,
  className,
  size = 'medium',
  variant = 'green',
  disabled = false,
  ...props
}: IconButtonProps & { $active: boolean }) => (
  <IconButton
    {...props}
    size={size}
    variant={variant}
    disabled={disabled}
    className={clsx(
      { [styles.viewTypeIconWrapperActive]: $active },
      className
    )}
  />
)

const FAVOURITES_LIST_NAME = SeoListName.FAVOURITES_LIST_NAME

interface ProfileFavouritesProps {
  productsData: ProductTypes[]
}

const ProfileFavourites = ({ productsData }: ProfileFavouritesProps) => {
  const [productsByCategory, setProductsByCategory] = useState<
    CategoryAccSearchTypes[] | []
  >([])
  const [viewMode, setViewMode] = useState('all')
  const router = useRouter()
  const pathname = usePathname()
  const urlSearchParams = useSearchParams()
  const searchParams = useMemo(
    () => ({
      sort_by: urlSearchParams?.get('sort_by') || undefined,
      order: urlSearchParams?.get('order') || undefined
    }),
    [urlSearchParams]
  )
  const [sorting, setSorting] = useState<string | string[]>('recommended')

  // Convert URLSearchParams to plain object for compatibility
  const queryParams = searchParamsToObject(urlSearchParams)

  // Create asPath equivalent for App Router
  const asPath = createAsPath(pathname, urlSearchParams)

  useEffect(() => {
    const sorted = sortProductsByCategory(productsData ?? [])

    setProductsByCategory(sorted)
  }, [productsData])

  const handleSortingChange = async (_, value: string | string[]) => {
    setSorting(value)

    const sortingOptions = getSortingOptionsBySelectValueForUpselling(value)

    const parsedQuery = parseQueryFilters(queryParams)

    if (value === 'recommended') {
      delete parsedQuery?.order
      delete parsedQuery?.sort_by
    }

    const params = qs.stringify(
      { ...parsedQuery, ...sortingOptions },
      { addQueryPrefix: true }
    )

    router.replace(`${pathname}${decodeURIComponent(params)}`)
  }

  useEffect(() => {
    const sortingToCheck = {
      sort_by: searchParams?.sort_by,
      order: searchParams?.order
    } as SortingOptionsTypes

    const sortingName = getSelectValueBySortingOptions(sortingToCheck)

    if (sortingName !== sorting) {
      setSorting(sortingName)
    }
  }, [searchParams, sorting])

  const handleViewChange = (viewType: string) => () => {
    setViewMode(viewType)
  }

  const handleCategoriesNavigation = () => {
    router.push(PATHS.categories)
  }

  return (
    <div>
      <div className={styles.title} id='profile_details_favourites'>
        Ulubione produkty
      </div>
      <>
        <div className={styles.sortingWrapper}>
          <ViewChangeWrapper>
            <ViewTypeIconWrapper
              $active={viewMode === 'all'}
              variant='transparent'
              onClick={handleViewChange('all')}
            >
              <IconList />
            </ViewTypeIconWrapper>
            <ViewTypeIconWrapper
              $active={viewMode === 'group'}
              variant='transparent'
              onClick={handleViewChange('group')}
            >
              <IconCategory />
            </ViewTypeIconWrapper>
          </ViewChangeWrapper>
          <div
            className={styles.sortingInputWrapper}
            {...addTestId('sorting-input')}
          >
            <div className={styles.sortingLabel}>Sortuj:</div>
            <SelectDropdown
              id='profile-favourites-sorting'
              variant='ghost'
              noMargin
              options={SORTING_OPTIONS}
              name='sorting'
              value={sorting}
              onChange={handleSortingChange}
            />
          </div>
        </div>
        {!productsData ||
          (productsData?.length === 0 ? (
            <>
              <div className={styles.emptyStateWrapper}>
                <IconMoodSad2
                  width={80}
                  height={80}
                  color={theme.colors.text.disabled}
                  strokeWidth={0.5}
                />
                <div className={styles.emptyStateHeader}>
                  Nie polubiłeś jeszcze żadnego produktu
                </div>
                <div>
                  Przejdź do listy kategorii i znajdź to co Cię interesuje
                </div>
                <div className={styles.emptyStateButtonWrapper}>
                  <Button
                    id='profile_favourites_categories_button'
                    variant='primaryOutline'
                    onClick={handleCategoriesNavigation}
                  >
                    Zobacz wszystkie kategorie
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              {productsData &&
                productsData?.length > 0 &&
                viewMode === 'all' && (
                  <div className={styles.content}>
                    {productsData?.map((product: ProductTypes, index) => (
                      <ProductCard
                        id={`profile_favourites_product_${product.id}`}
                        productPath={product?.slug || ''}
                        key={product.id}
                        product={product}
                        seoButtonPlace={asPath}
                        listName={FAVOURITES_LIST_NAME}
                        idxOnList={index}
                      />
                    ))}
                  </div>
                )}
              {productsByCategory.length > 0 && viewMode === 'group' && (
                <div className={styles.listWrapper}>
                  {productsByCategory?.map(
                    (category: CategoryAccSearchTypes) => {
                      const items = category?.items || []

                      return (
                        <div key={category?.categoryId}>
                          <div className={styles.categoryTitle}>{category.categoryName}</div>
                          <div className={styles.content}>
                            {items.map((product: ProductTypes, index) => (
                              <ProductCard
                                id={`profile_favourites_product_by_category_${product.id}`}
                                productPath={product?.slug || ''}
                                key={product.id}
                                product={product}
                                seoButtonPlace={asPath}
                                listName={FAVOURITES_LIST_NAME}
                                idxOnList={index}
                              />
                            ))}
                          </div>
                        </div>
                      )
                    }
                  )}
                </div>
              )}
            </>
          ))}
      </>
    </div>
  )
}

export default ProfileFavourites
